import { ReactNode } from "react";

import { useThemeContext } from "../../../context/ThemeContext";

import { Card } from "../Card";
import { CardLabel } from "../CardLabel";

import styles from "./DetailsCard.module.scss";

import type { DetailsCardProps } from "../types";

interface RowItemProps {
  rowKey: string;
  value: ReactNode;
}

function RowItem({ rowKey, value }: RowItemProps) {
  const theme = useThemeContext();
  const hasLink = typeof value === "string" && value.startsWith("http");
  const cutLink = hasLink && (value as string).replace("https://", "");

  return (
    <div
      className={hasLink ? styles.linkRow : styles.row}
      style={{ borderColor: theme.rule }}
    >
      <span style={{ color: theme.muted }}>{rowKey}</span>
      {hasLink ? (
        <span
          style={{
            color: theme.acc,
            borderBottom: `1px solid ${theme.acc}`,
            paddingBottom: 1,
          }}
        >
          {cutLink || value} ↗
        </span>
      ) : (
        <span style={{ color: theme.fg, textAlign: "right" }}>{value}</span>
      )}
    </div>
  );
}

export function DetailsCard({
  label,
  labelType,
  variant,
  rows,
  items,
}: DetailsCardProps) {
  const theme = useThemeContext();

  return (
    <Card theme={theme}>
      <CardLabel cardLabelType={labelType} cardLabelText={label} />
      {variant === "rows" && rows ? (
        Object.entries(rows).map(([key, value]) => (
          <RowItem key={key} rowKey={key} value={value} />
        ))
      ) : variant === "list" && items ? (
        <ul className={styles.list}>
          {items.map((item, i) => (
            <li
              key={i}
              className={styles.item}
              style={{
                borderBottomColor:
                  i === items.length - 1 ? "transparent" : theme.rule,
              }}
            >
              <div style={{ color: theme.fg }}>{item.title}</div>
              {item.subtitle && (
                <div className={styles.subtitle} style={{ color: theme.muted }}>
                  {item.subtitle}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
